import { FC } from 'react'
import { DataCell, HeaderCell, Table, TableBody, TableHead, TableRow } from '@entur/table'
import { colors } from '@entur/tokens'
import { SavingTicket } from '../../../api-clients/api-types'
import { toPrintFormat } from '../../../utils/date-utils'

type TravelTableProps = {
  tickets: SavingTicket[]
  year: number
  month: number
}

type TravelTableData = {
  orderId: string
  type: 'Tjeneste' | 'Privat'
  travellerName: string
  startDate: string
  endDate: string
  description: string
  price: string
  ticketPrice: string
  cancellationDate: string
}

export const TravelTable: FC<TravelTableProps> = (props) => {
  const getMonthlySaving = (ticket: SavingTicket, year: number, month: number) => {
    if (ticket.savingDistribution.length === 0) {
      return undefined
    }

    const savingDistribution = ticket.savingDistribution.find(
      (saving) => saving.year === year && saving.month === month
    )

    if (savingDistribution === undefined) return undefined

    return {
      saving: savingDistribution.details.saving,
      ticketPrice: savingDistribution.details.ticketPrice,
      additionalProduct: savingDistribution.details.additionalProduct,
    }
  }

  const getPricePaid = (ticket: SavingTicket, year: number, month: number) => {
    const monthlySaving = getMonthlySaving(ticket, year, month)
    const totalSaving = ticket.totalSaving

    const saving = monthlySaving !== undefined ? monthlySaving.saving : totalSaving.saving
    const ticketPrice = monthlySaving !== undefined ? monthlySaving.ticketPrice : totalSaving.ticketPrice
    const additionalProduct =
      monthlySaving !== undefined ? monthlySaving.additionalProduct : totalSaving.additionalProduct

    const savingsNum = Number(saving.split('.00')[0])
    const ticketPriceNum = Number(ticketPrice.split('.00')[0])
    const additionalProductNum = additionalProduct ? Number(additionalProduct.split('.00')[0]) : 0

    if (ticket.isWorkTrip) {
      return `${-additionalProductNum}.00`
    } else return `${ticketPriceNum - savingsNum}.00`
  }

  const getTicketPrice = (ticket: SavingTicket, year: number, month: number) => {
    const monthlySaving = getMonthlySaving(ticket, year, month)
    const totalSaving = ticket.totalSaving

    return monthlySaving !== undefined ? monthlySaving.ticketPrice : totalSaving.ticketPrice
  }

  const savingTicketToTableData: (ticket: SavingTicket) => TravelTableData = (ticket: SavingTicket) => {
    return {
      orderId: ticket.orderId,
      type: ticket.isWorkTrip ? 'Tjeneste' : 'Privat',
      travellerName: ticket.name === undefined ? '' : ticket.name,
      startDate: toPrintFormat(ticket.travelDate),
      endDate: toPrintFormat(ticket.lastTravelDate),
      description: ticket.pathDescription,
      price: getPricePaid(ticket, props.year, props.month),
      ticketPrice: getTicketPrice(ticket, props.year, props.month),
      cancellationDate:
        ticket.effectiveCancellationDate !== undefined ? toPrintFormat(ticket.effectiveCancellationDate) : '',
    }
  }

  const travelTableRows = props.tickets.map((ticket, index) => {
    const tableData = savingTicketToTableData(ticket)
    return (
      <TableRow
        key={index}
        style={
          ticket.effectiveCancellationDate !== undefined ? { backgroundColor: colors.validation.lavaTint } : {}
        }
      >
        <DataCell>{tableData.orderId}</DataCell>
        <DataCell>{tableData.type}</DataCell>
        <DataCell>{tableData.travellerName}</DataCell>
        <DataCell>{tableData.startDate}</DataCell>
        <DataCell>{tableData.endDate}</DataCell>
        <DataCell>{tableData.description}</DataCell>
        <DataCell>{tableData.price}</DataCell>
        <DataCell>{tableData.ticketPrice}</DataCell>
        <DataCell>{tableData.cancellationDate}</DataCell>
      </TableRow>
    )
  })

  return (
    <div style={{ marginTop: '0.25rem' }}>
      <Table spacing={'small'}>
        <TableHead>
          <TableRow>
            <HeaderCell>Ordrenummer</HeaderCell>
            <HeaderCell>Type</HeaderCell>
            <HeaderCell>Reisende</HeaderCell>
            <HeaderCell>Startdato</HeaderCell>
            <HeaderCell>Sluttdato</HeaderCell>
            <HeaderCell>Beskrivelse</HeaderCell>
            <HeaderCell>Pris</HeaderCell>
            <HeaderCell>Billettpris</HeaderCell>
            <HeaderCell>Kansellert</HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>{travelTableRows}</TableBody>
      </Table>
    </div>
  )
}
